
import { FormattedMessage } from 'react-intl';
import { Button, Tabs } from 'antd';
import { useParams, useLocation } from 'react-router-dom';

import ArticlesTab from './ArticlesTab/ArticlesTab';
import ConversationTab from './ConversationTab/ConversationTab';
import FormPopup from '../../components/Popup/FormPopup';
import Icon from '../../components/Icon/Icon';
import ProfileCard from '../../components/Cards/ProfileCard';
import ProfileTab from './ProfileTab/ProfileTab';
import RatingPopup from '../../components/RatingPopup/RatingPopup';
import Reviews from '../../components/Reviews/Reviews';
import ScheduleEvent from '../../components/ScheduleEvent/ScheduleEvent';
import NetworkTab from './NetworkTab/NetworkTab';
import UserAverageRating from '../../components/UserAverageRating/UserAverageRating';

import useUserProfile from './useUserProfile';

import { ContactStatus } from './UserProfile.model';

import styles from './UserProfile.module.scss';
import { useEffect } from 'react';
import { useAuthorizationDataContext } from "../../Helpers/AuthorizationDataProvider";

function useQuery() {
	return new URLSearchParams(useLocation().search);
}

const UserProfile = () => {
	const { TabPane } = Tabs;
	const { id } = useParams<{ id: string }>();
	const tab = useQuery().get('tab');

	const {
		acceptContactRequest,
		formatDate,
		isAuthorized,
		navigateTo,
		onChangeTab,
		onClickJoinMeeting,
		onClickReviewer,
		onClickStartMeeting,
		rejectContactRequest,
		setActiveTab,
		sendContactRequest,
		state,
		toggleContactPopup,
		toggleRatingPopup,
		toggleRejectPopup,
		toggleScheduleEventPopup,
		userId,
	} = useUserProfile({ id: id });

	const {
		activeTab,
		averageScore,
		contactStatus,
		i18nCountries,
		mentors,
		profile,
		reviews,
		rocketRoomId,
		showContactPopup,
		showJoinCall,
		showRatingPopup,
		showRejectPopup,
		showScheduleEventPopup,
	} = state;

	const { role } = useAuthorizationDataContext();

	useEffect(() => {
		if (tab === 'conversation')
			setActiveTab('4')
	}, [id])

	const showStartCallButtons = isAuthorized(['InitiateCall']) && rocketRoomId && activeTab === '4';

	const tabBarExtraContent = {
		right:
			<>
				<div className={`${styles.tabBarExtraContent}`}>{showStartCallButtons &&
					<>{!showJoinCall ?
						<>
							<Button size="large" onClick={onClickStartMeeting}><FormattedMessage id="buttons.startCall" /></Button>
							<Button size="large" type="primary" onClick={toggleScheduleEventPopup}><FormattedMessage id="buttons.scheduleCall" /></Button>
						</> :
						<>
							<Button size="large" onClick={onClickJoinMeeting}><FormattedMessage id="buttons.joinCall" /></Button>
							<Button size="large" type="primary" onClick={toggleScheduleEventPopup}><FormattedMessage id="buttons.scheduleCall" /></Button>
						</>}
					</>}
				</div>
				<div className={`${styles.tabBarExtraContent} ${styles.displayBlock}`}>{!showStartCallButtons &&
					<>{(showJoinCall && rocketRoomId && activeTab === '4') ?
						<Button onClick={onClickJoinMeeting}><FormattedMessage id="buttons.joinCall" /></Button> : <></>}
					</>}
				</div>
			</>
	};

	const renderContactButton = () => {
		switch (contactStatus) {
			case 'SendRequest':
				return (<Button size="large" type="primary" onClick={toggleContactPopup}>
					<FormattedMessage id="buttons.contact" /> <FormattedMessage id={`role.${profile?.role}`} />
				</Button>);
			case 'Pending':
			case 'Rejected':
				return (
					<Button type="primary" size="large" disabled>
						<FormattedMessage id={`contactStatus.${contactStatus}`} />
					</Button>);
			case 'ResolveRequest':
				return (
					<div className={styles.buttonWrapper}>
						<Button className={`${styles.rezolveRequestBtns} ${styles.accentButtonDefault}`} size="middle" type="default" onClick={toggleRejectPopup}>
							<FormattedMessage id="buttons.reject" />
						</Button>
						<Button className={`${styles.rezolveRequestBtns} ${styles.btnWithIcon} ${styles.greenButtonPrimary}`} size="middle" type="primary" onClick={acceptContactRequest}>
							<Icon name="check" color="white" width="12px" height="13px" />
							<FormattedMessage id="buttons.accept" />
						</Button>
					</div>);
		}
	}

	const renderProfileCard = () => (
		<ProfileCard
			actions={[
				<div className={styles.actions}>
					{averageScore && <UserAverageRating score={averageScore} />}
					{profile?.role === 'Mentor' && isAuthorized(['RateUser']) && contactStatus === 'SeeChat' && <Button size="large" onClick={toggleRatingPopup}><FormattedMessage id="buttons.rateExperience" /></Button>}
					<div className={styles.availability}>
						<h3><FormattedMessage id="userProfile.availableFor" /><Icon color="text" height="8" name="chevron-right" /></h3>
						<div className={styles.iconsWrapper}>
							{profile?.isReachableByText ? <Icon color="green" name="comment-alt-lines" type="solid" /> : <Icon color="light" name="comment-alt-slash" type="solid" />}
							{profile?.isReachableByCall ? <>
								<Icon color="green" name="phone" type="solid" />
								<Icon color="green" name="video" type="solid" />
							</> : <>
								<Icon color="light" name="phone-slash" type="solid" />
								<Icon color="light" name="video-slash" type="solid" />
							</>}

						</div>
					</div>
					{renderContactButton()}
				</div>
			]}
			fitContainer
			showData
			data={{
				currentPosition: profile?.currentPosition,
				education: profile?.education,
				imageId: profile?.imageId,
				languages: profile?.languages,
				location: profile?.location,
				name: profile?.name || '',
				pronouns: profile?.pronouns,
				researchAreas: profile?.researchAreas,
				workExperience: profile?.workExperience,
			}}>
		</ProfileCard>
	);

	return (
		<div className={styles.pageContainer}>
			<div
				className={`${styles.pageSider} ${styles.topBorder} ${styles.noPadding}`}
			>
				{profile && renderProfileCard()}
			</div>

			<div className={styles.pageContent}>
				<div
					className={`${styles.mobileTopper} ${styles.topBorder} ${styles.noPadding}`}
				>
					{profile && renderProfileCard()}
				</div>
				{profile && (
					<div className={styles.content}>
						{showStartCallButtons && (
							<div className={styles.tabBarExtraContentMobile}>
								<Button onClick={onClickStartMeeting}>
									<FormattedMessage id="buttons.startCall" />
								</Button>
								<Button type="primary" onClick={toggleScheduleEventPopup}>
									<FormattedMessage id="buttons.scheduleCall" />
								</Button>
							</div>
						)}

						<Tabs
							activeKey={activeTab}
							className="navigator"
							defaultActiveKey={tab ? '4' : '1'}
							tabBarExtraContent={tabBarExtraContent}
							onChange={onChangeTab}
						>
							<TabPane
								tab={
									<>
										<span>
											<span className={styles.tabPaneName}>
												{profile.name.slice(0, profile.name.indexOf(' '))}'s{' '}
											</span>
											<FormattedMessage id="navigation.profile" />
										</span>
										<span className={styles.tabDivider}>/</span>
									</>
								}
								key="1"
							>
								<ProfileTab
									i18nCountries={i18nCountries}
									id={id}
									introduction={profile.introduction}
									mentoringScopes={profile.mentoringScopes}
									reviews={
										<Reviews
											onClickReviewer={
												role !== 'ApplicantMentee' ? onClickReviewer : undefined
											}
											reviews={reviews}
										/>
									}
									role={profile.role}
									showReviews={profile.role === 'Mentor'}
									formatDate={formatDate}
								/>
							</TabPane>
							<TabPane
								tab={
									<>
										<span>
											<FormattedMessage id="navigation.articles" />
										</span>
										{isAuthorized(['ViewUserNetwork']) && (
											<span className={styles.tabDivider}>/</span>
										)}
									</>
								}
								key="2"
							>
								<ArticlesTab i18nCountries={i18nCountries} id={id} />
							</TabPane>
							{isAuthorized(['ViewUserNetwork']) && (
								<TabPane
									tab={
										<>
											<span>
												<FormattedMessage id="navigation.network" />
											</span>
											{contactStatus === ContactStatus.SeeChat && (
												<span className={styles.tabDivider}>/</span>
											)}
										</>
									}
									key="3"
								>
									<NetworkTab id={id} userId={userId} navigateTo={navigateTo} />
								</TabPane>
							)}

							{rocketRoomId && (
								<TabPane
									tab={
										<>
											<span>
												<FormattedMessage id="navigation.conversation" />
											</span>
										</>
									}
									key="4"
								>
									<ConversationTab
										roomId={rocketRoomId}
									/>
								</TabPane>
							)}
						</Tabs>
					</div>
				)}
			</div>
			{showContactPopup && (
				<FormPopup
					actions={[
						{ onClick: toggleContactPopup, title: 'cancel', type: 'default' },
						{ isSubmitButton: true, title: 'sendRequest', type: 'primary' },
					]}
					fields={[
						{
							id: 'requestMessage',
							layout: 'w100',
							rules: { required: true },
							type: 'textArea',
						},
					]}
					title={{ action: 'send', target: 'messageRequest' }}
					onCancel={toggleContactPopup}
					onSubmitForm={sendContactRequest}
				/>
			)}
			{showRejectPopup && (
				<FormPopup
					actions={[
						{ onClick: toggleRejectPopup, title: 'cancel', type: 'default' },
						{ isSubmitButton: true, title: 'sendMessage', type: 'primary' },
					]}
					fields={[
						{
							id: 'rejectionMessage',
							layout: 'w100',
							rules: { required: true },
							type: 'textArea',
						},
						{
							id: 'userSuggested',
							layout: 'w100',
							options: {
								filterOption: (input: any, option: any) => {
									return option.children.props.children[1].props.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
								},
								optionList: mentors
									? mentors.map((mentor) => ({
										label: (
											<div className={styles.userOption} >
												<div className={styles.avatar}>
													{mentor.imageId ?
														<img src={mentor.imageId} alt="" /> :
														<Icon
															color="white"
															height="12px"
															name="user"
															width="12px"
														/>}
												</div>
												<span>{`${mentor.firstName} ${mentor.lastName}`}</span>

											</div>
										),
										value: mentor.userId,
									}))
									: [],
								type: 'codeBased',
							},
							type: 'select',
						},
					]}
					title={{ action: 'send', target: 'rejectionMessage' }}
					onCancel={toggleRejectPopup}
					onSubmitForm={rejectContactRequest}
				/>
			)}
			{showScheduleEventPopup && (
				<ScheduleEvent userId={userId} onClose={toggleScheduleEventPopup} />
			)}
			{showRatingPopup && (
				<RatingPopup
					firstName={profile?.firstName}
					imageUrl={profile?.imageId}
					lastName={profile?.lastName}
					userId={id}
					onClose={toggleRatingPopup}
				/>
			)}
		</div>
	);
}

export default UserProfile;